import React, { useEffect, useState } from 'react'
import { AppProvider, useApp } from '../lib/context'
import { I18nProvider } from '../lib/i18n'
import { useHealth, useAlerts, registerSyncListener, triggerForceSync } from '../lib/api'
import Layout from '../ui/Layout'
import AlertBanner from '../ui/AlertBanner'
import AlertPanel from '../ui/AlertPanel'
import SearchModal from '../ui/SearchModal'
import Overview from '../ui/views/Overview'
import Journal from '../ui/views/Journal'
import Kanban from '../ui/views/Kanban'
import Compare from '../ui/views/Compare'
import Evolution from '../ui/views/Evolution'
import GitDiff from '../ui/views/GitDiff'
import Progress from '../ui/views/Progress'
import Tasks from '../ui/views/Tasks'
import Tokens from '../ui/views/Tokens'
import NotificationManager from '../ui/NotificationManager'

function ViewRouter() {
  const { activeView } = useApp()
  switch (activeView) {
    case 'overview':  return <Overview />
    case 'journal':   return <Journal />
    case 'kanban':    return <Kanban />
    case 'compare':   return <Compare />
    case 'evolution': return <Evolution />
    case 'progress':  return <Progress />
    case 'tasks':     return <Tasks />
    case 'gitdiff':   return <GitDiff />
    case 'tokens':    return <Tokens />
    default:          return <Overview />
  }
}

function DashboardInner() {
  const { connected: healthConnected, refetch: refetchHealth } = useHealth()
  const { data: alertsData, refetch: refetchAlerts } = useAlerts()
  const { setView } = useApp()
  const [lastSync, setLastSync] = useState<Date | null>(null)

  const alerts = alertsData?.alerts ?? []
  const connected = healthConnected

  /* Apply theme on mount */
  useEffect(() => {
    const saved = localStorage.getItem('cc-theme') ?? 'dark'
    document.documentElement.setAttribute('data-theme', saved === 'light' ? 'light' : '')
  }, [])

  /* Enregistrer le listener force-sync (touche R) */
  useEffect(() => {
    const unsub = registerSyncListener(() => {
      refetchHealth()
      refetchAlerts()
      setLastSync(new Date())
    })
    return unsub
  }, [refetchHealth, refetchAlerts])

  /* Mettre à jour lastSync à chaque reconnexion */
  useEffect(() => {
    if (connected) setLastSync(new Date())
  }, [connected])

  return (
    <Layout connected={connected} lastSync={lastSync} alerts={alerts}>
      <AlertBanner alerts={alerts} onViewAll={() => setView('overview')} />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
          <ViewRouter />
        </div>
        <AlertPanel alerts={alerts} onAck={refetchAlerts} />
      </div>
      <SearchModal />
      <NotificationManager />
    </Layout>
  )
}

function LoginPage({ onLogin }: { onLogin: (t: string) => void }) {
  const [val, setVal] = React.useState('')
  const [err, setErr] = React.useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const t = val.trim()
    if (!t) return
    const res = await fetch('/api/health', { headers: { Authorization: `Bearer ${t}` } })
    if (res.ok) { onLogin(t) } else { setErr(true) }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1117' }}>
      <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 12, padding: '2rem', width: 360 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
          <img src="/favicon-kdb.svg" width={36} height={36} alt="" />
          <div>
            <div style={{ color: '#e6edf3', fontWeight: 700, fontSize: 16 }}>KayTech Dashboard</div>
            <div style={{ color: '#8b949e', fontSize: 12 }}>Tableau de bord agents</div>
          </div>
        </div>
        <form onSubmit={submit}>
          <label style={{ color: '#8b949e', fontSize: 13, display: 'block', marginBottom: 6 }}>Token d'accès</label>
          <input
            type="password"
            value={val}
            onChange={e => { setVal(e.target.value); setErr(false) }}
            placeholder="Coller le token VPS..."
            style={{ width: '100%', padding: '10px 12px', background: '#0d1117', border: `1px solid ${err ? '#f85149' : '#30363d'}`, borderRadius: 6, color: '#e6edf3', fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
            autoFocus
          />
          {err && <div style={{ color: '#f85149', fontSize: 12, marginTop: 6 }}>Token invalide</div>}
          <button
            type="submit"
            style={{ marginTop: 16, width: '100%', padding: '10px', background: '#8B0000', border: 'none', borderRadius: 6, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Accéder au dashboard
          </button>
        </form>
      </div>
    </div>
  )
}

export default function Dashboard() {
  const [token, setToken] = React.useState(localStorage.getItem('cc-token') ?? '')

  function handleLogin(t: string) {
    localStorage.setItem('cc-token', t)
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    import('../lib/api').then(m => m.setToken(t))
    setToken(t)
  }

  if (!token) return <LoginPage onLogin={handleLogin} />

  return (
    <AppProvider>
      <I18nProvider>
        <DashboardInner />
      </I18nProvider>
    </AppProvider>
  )
}
